import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { useTranslation } from "react-i18next";

interface Props {
  archived: boolean | null;
  onArchivedChange: (_value: boolean | null) => void;
}

/**
 * ArchivedToggleFilter component
 * Provides a checkbox to filter tenders by archived status
 * Toggles between showing archived (true) or non-archived (null/false, default)
 * By default, paginated tenders returns only non-archived tenders
 * Note: This component only updates local dialog state. The filter is submitted
 * when the user clicks the confirm button in the dialog.
 *
 * @param {Object} props - Component props
 * @param {boolean | null} props.archived - Current archived filter value
 * @param {Function} props.onArchivedChange - Callback to update archived filter value
 * @returns {JSX.Element} The archived filter checkbox component
 */
const ArchivedToggleFilter = ({ archived, onArchivedChange }: Props) => {
  // Hooks
  const { t } = useTranslation(["components"]);

  const isArchivedFilterActive = archived === true;

  const handleArchivedToggle = (checked: boolean) => {
    const newArchivedValue: boolean | null = checked ? true : null;
    onArchivedChange(newArchivedValue);
  };

  return (
    <Field orientation="horizontal">
      <Checkbox
        id="archived-filter"
        checked={isArchivedFilterActive}
        onCheckedChange={handleArchivedToggle}
      />
      <FieldLabel
        htmlFor="archived-filter"
        className="font-normal cursor-pointer"
      >
        {t("filters.tenders.archived.showarchivedchances")}
      </FieldLabel>
    </Field>
  );
};

export default ArchivedToggleFilter;
